import React, { useState, useEffect, useContext } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AuthContext } from '../context/AuthContext';
import { getCustomer } from '../utils/api';

const TermsOfServicePage = () => {
        const { user } = useContext(AuthContext);
        const [customer, setCustomer] = useState(null);
        const [error, setError] = useState(null);
        const [loading, setLoading] = useState(true);

        useEffect(() => {
                const fetchData = async () => {
                        if (user && user.customerId) {
                                try {
                                        const customerRes = await getCustomer(user.customerId);
                                        if (!customerRes.ok)
                                                throw new Error("Customer not found");
                                        const customerData = await customerRes.json();
                                        setCustomer(customerData.data);
                                } catch (err) {
                                        setError("Error loading data");
                                        console.error(err);
                                } finally {
                                        setLoading(false);
                                }
                        } else {
                                setLoading(false);
                        }
                };
                fetchData();
        }, [user]);

        if (loading) {
                return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
        }

        if (error) {
                return <div className="page-gradient min-h-screen flex items-center justify-center text-status-critical">{error}</div>;
        }

        return (
                <div className="page-gradient min-h-screen">
                        <Header customer={customer} />
                        <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
                                <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 card-shadow rounded-2xl">
                                        <h1 className="text-3xl font-bold mb-4">Terms of Service</h1>
                                        <p className="text-sm text-gray-600 mb-6"><strong>Last Updated: December 12, 2025</strong></p>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">1. Agreement to Terms</h2>
                                                <p className="mb-4">
                                                        By accessing or using CleverPlant Portal ("Service"), you agree to these Terms of Service. If you do not agree, do not use the Service.
                                                </p>
                                                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                                        <p className="font-semibold mb-2">Service Provider:</p>
                                                        <p>Move Innovation ApS<br />
                                                                Generatorvej 8A, 2<br />
                                                                2860 Søborg<br />
                                                                CVR: 32777066<br />
                                                                Email: info@moveinnovation.dk<br />
                                                                Phone: +45 32205060</p>
                                                </div>
                                        </section>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">2. Description of Service</h2>
                                                <p className="mb-2">CleverPlant provides IoT-based plant monitoring services, including:</p>
                                                <ul className="list-disc list-inside mb-4 space-y-1">
                                                        <li>Environmental sensor data collection (temperature, moisture, light, nutrition)</li>
                                                        <li>Web-based monitoring dashboard</li>
                                                        <li>Device management and provisioning</li>
                                                        <li>Data visualization and health metrics</li>
                                                </ul>
                                        </section>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">3. User Accounts</h2>
                                                <div className="space-y-2 mb-4">
                                                        <p>3.1. You must provide accurate registration information and keep it updated.</p>
                                                        <p>3.2. You are responsible for maintaining the confidentiality of your account credentials.</p>
                                                        <p>3.3. You must notify us immediately of any unauthorized account access.</p>
                                                        <p>3.4. You must be at least 18 years old to create an account.</p>
                                                </div>
                                        </section>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">4. Acceptable Use</h2>
                                                <p className="mb-2">You agree NOT to:</p>
                                                <ul className="list-disc list-inside mb-4 space-y-1">
                                                        <li>Use the Service for illegal purposes</li>
                                                        <li>Attempt to gain unauthorized access to our systems</li>
                                                        <li>Interfere with or disrupt the Service</li>
                                                        <li>Reverse engineer or attempt to extract source code</li>
                                                        <li>Resell or redistribute the Service without authorization</li>
                                                </ul>
                                        </section>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">5. Data and Privacy</h2>
                                                <div className="space-y-2 mb-4">
                                                        <p>5.1. Our processing of personal data is governed by our Privacy Policy, which complies with GDPR (EU Regulation 2016/679).</p>
                                                        <p>5.2. You retain ownership of your sensor data.</p>
                                                        <p>5.3. We process data as a data processor on your behalf for Service provision.</p>
                                                        <p>5.4. Data retention periods are specified in our Privacy Policy.</p>
                                                </div>
                                        </section>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">6. Subscription and Payment</h2>
                                                <div className="space-y-2 mb-4">
                                                        <p>6.1. Service fees are specified in your subscription plan.</p>
                                                        <p>6.2. Payments are processed monthly/annually in advance.</p>
                                                        <p>6.3. All prices are in DKK and include Danish VAT (moms) where applicable.</p>
                                                </div>
                                        </section>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">7. Service Availability</h2>
                                                <div className="space-y-2 mb-4">
                                                        <p>7.1. We strive for high availability but do not guarantee uninterrupted service.</p>
                                                        <p>7.2. We may perform scheduled maintenance with advance notice.</p>
                                                        <p>7.3. We are not liable for service interruptions due to circumstances beyond our control.</p>
                                                </div>
                                        </section>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">8. Intellectual Property</h2>
                                                <div className="space-y-2 mb-4">
                                                        <p>8.1. The Service, including software, design, and content, is owned by CleverPlant and protected by intellectual property laws.</p>
                                                        <p>8.2. You receive a limited, non-exclusive, non-transferable license to use the Service.</p>
                                                </div>
                                        </section>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">10. Warranty Disclaimer</h2>
                                                <p className="mb-4">
                                                        THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. We do not warrant that the Service will be error-free or meet your specific requirements.
                                                </p>
                                                <p className="mb-4">
                                                        <strong>Consumer Note:</strong> If you are a consumer under Danish law, you have statutory rights that cannot be excluded (Købeloven).
                                                </p>
                                        </section>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">11. Termination</h2>
                                                <div className="space-y-2 mb-4">
                                                        <p>11.1. You may terminate your account at any time through account settings or by contacting support.</p>
                                                        <p>11.2. We may suspend or terminate your account for:</p>
                                                        <ul className="list-disc list-inside ml-6 space-y-1">
                                                                <li>Violation of these Terms</li>
                                                                <li>Non-payment</li>
                                                                <li>Illegal activity</li>
                                                        </ul>
                                                        <p>11.3. Upon termination, your right to use the Service ceases immediately.</p>
                                                        <p>11.4. You may request a copy of your data within 30 days of termination.</p>
                                                </div>
                                        </section>

                                        <section className="mb-8">
                                                <h2 className="text-2xl font-bold mb-3">12. Changes to Terms</h2>
                                                <div className="space-y-2 mb-4">
                                                        <p>12.1. We may modify these Terms with 30 days' notice.</p>
                                                        <p>12.2. Continued use after changes constitutes acceptance.</p>
                                                        <p>12.3. If you do not agree to changes, you may terminate your account.</p>
                                                </div>
                                        </section>
                                </div>
                        </main>
                        <Footer />
                </div>
        );
};

export default TermsOfServicePage;
